import React from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/use-auth'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading, isAuthenticated } = useAuth()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="flex items-center gap-3" role="status" aria-live="polite">
          <Loader2 className="h-5 w-5 animate-spin text-brand-600" aria-hidden="true" />
          <p className="text-sm text-foreground/70 tracking-tight">Tuning the airwaves…</p>
        </div>
      </div>
    )
  }

  // Check if email verification is required from URL params
  const emailVerificationRequired = searchParams.get('email_verification_required')
  const email = searchParams.get('email')
  if (emailVerificationRequired === 'true') {
    const redirectUrl = email ? `/email-verification-required?email=${encodeURIComponent(email)}` : '/email-verification-required'
    return <Navigate to={redirectUrl} replace />
  }

  // If not authenticated, redirect to sign in
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />
  }

  // If authenticated but email not verified, redirect to verification required page
  if (user && !user.emailVerified) {
    return <Navigate to="/email-verification-required" replace />
  }

  return <>{children}</>
}

export default ProtectedRoute
